// Stripe のカード決済。
//
// ■ カード番号はこちらのサーバを通らない
// サーバが申込から金額を決めて Stripe の支払いページ（Checkout）を作り、会員をそこへ移す。
// カードの入力・3Dセキュアは Stripe の画面で行われる。
//
// ■ 戻ってきた後
// 支払いページはイベントの画面（?pay=stripe&session=…）へ戻す。反映は EventDetail が行う
// （finishStripeReturn）。反映は Webhook でも届くので、戻らなかった場合も記録される。
// 正本: docs/plans/stripe.md
import { useState } from "react";
import { api } from "../api";

/** 支払いページは Stripe のものだけ（それ以外の宛先には移さない） */
const ALLOWED_CHECKOUT = /^https:\/\/checkout\.stripe\.com\//;

export function StripePayButton({ applicationId }: { applicationId: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pay = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ url: string }>("/payments/stripe/checkout", { applicationId });
      if (!ALLOWED_CHECKOUT.test(r.url)) throw new Error("支払いページの宛先が想定外です");
      window.location.assign(r.url);
      // 画面が切り替わるまでボタンは押せないまま（二重に支払いページを作らない）
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1">
      <button type="button" className="btn-outline" disabled={busy} onClick={pay}>
        {busy ? "支払いページを準備中…" : "クレジットカードで支払う（Stripe）"}
      </button>
      <p className="text-xs text-tora-ink/60">
        Stripe の支払いページに移ります（カード番号はこのサイトには送られません）。ページは 30 分で期限が切れます。
      </p>
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}

/**
 * 支払いページから戻ってきたときの反映。結果の文言を返す（支払えたかどうかはサーバが決める）。
 *
 * `retry` … 通信の途中切れ・サーバの一時的な障害で、結果がまだ分からない。もう一度頼める（何度頼んでも結果は同じ）。
 */
export async function finishStripeReturn(
  sessionId: string,
): Promise<{ ok: boolean; message: string; retry?: boolean }> {
  try {
    const r = await api.post<{ status: string }>(
      `/payments/stripe/sessions/${encodeURIComponent(sessionId)}/sync`,
      {},
    );
    return r.status === "succeeded"
      ? { ok: true, message: "お支払いが完了しました。" }
      : { ok: true, message: "お支払いを受け付けました。反映まで少しかかることがあります。" };
  } catch (e) {
    const code = (e as Error & { code?: string }).code;
    if (code === "not_paid") return { ok: false, message: "お支払いは完了していません（請求されていません）。" };
    const status = (e as Error & { status?: number }).status;
    if (status === undefined || status >= 500)
      return {
        ok: false,
        retry: true,
        message: "お支払いの結果を確認できませんでした。通信の状態を確かめて「もう一度確認する」を押してください。",
      };
    return { ok: false, message: (e as Error).message };
  }
}
